<template>
  <div class="admin-licenses-v3">
    <v-alert v-if="loadError" type="error">{{ loadError }}</v-alert>
    <v-alert v-if="message" type="success" dismissible @input="message = ''">
      {{ message }}
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="br">Search subscription licenses</v-card-title>
      <v-card-text>
        <v-form ref="searchForm" @submit.prevent="searchLicenses">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.license_sn"
                label="License s/n"
                maxlength="33"
                :disabled="saving || resolvingUser"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.user_id"
                label="User ID"
                type="number"
                min="1"
                step="1"
                :rules="optionalIdRules"
                :disabled="saving || resolvingUser"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.user_email"
                label="User email"
                maxlength="254"
                :disabled="saving || resolvingUser"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.user_name"
                label="User name"
                maxlength="100"
                :disabled="saving || resolvingUser"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filters.v3_enabled"
                label="Enabled"
                :items="booleanOptions"
                :disabled="saving || resolvingUser"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filters.active_subscriptions"
                label="Active subscriptions"
                :items="booleanOptions"
                :disabled="saving || resolvingUser"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                class="mr-2"
                :loading="loading"
                :disabled="saving || resolvingUser"
              >Search</v-btn>
              <v-btn text :disabled="loading || saving || resolvingUser" @click="resetSearch">
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
        <div>
          Active subscriptions means at least one subscription covers today in UTC,
          including its start and end dates, whether or not the license is enabled.
        </div>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="licenses"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="elevation-1"
      :no-data-text="
        hasSearched
          ? 'No v3 licenses found.'
          : 'Click Search to load licenses.'
      "
      @dblclick:row="onDoubleClick"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title><h2 class="br">Subscription Licenses</h2></v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            small
            :loading="resolvingUser"
            :disabled="loading || saving || resolvingUser"
            @click="openTrial"
          >New trial license</v-btn>
          <v-btn
            icon
            :disabled="!hasSearched || loading || saving || resolvingUser"
            aria-label="Refresh licenses"
            @click="initialize"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.user_name="{ item }">
        <div class="py-2">
          <div>{{ item.user_name || "—" }}</div>
          <div>{{ item.user_email || "—" }}</div>
          <div class="text-caption">User ID: {{ item.user_id }}</div>
        </div>
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:item.v3_enabled="{ item }">
        <v-chip
          small
          dark
          :color="item.v3_enabled ? 'green darken-2' : 'red darken-2'"
        >
          {{ item.v3_enabled ? "Yes" : "No" }}
        </v-chip>
      </template>

      <template v-slot:item.active_subscriptions="{ item }">
        <v-chip small dark :color="item.active_subscriptions ? 'green darken-2' : 'red darken-2'">
          {{ item.active_subscriptions ? "Yes" : "No" }}
        </v-chip>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip
          small
          dark
          :color="item.status ? 'green darken-2' : 'red darken-2'"
          title="Active means enabled with a current subscription. BR release eligibility is checked separately."
        >
          {{ item.status ? "Active" : "Inactive" }}
        </v-chip>
      </template>

      <template v-slot:item.subscription="{ item }">
        <div v-if="item.subscription" class="py-2">
          <div>{{ formatBandwidth(item.subscription.bandwidth) }}</div>
          <div>
            {{ formatDate(item.subscription.starts_at) }} —
            {{ formatDate(item.subscription.ends_at) }}
          </div>
          <div class="text-caption">
            {{ formatCutoff(item.subscription.br_release_cutoff) }}
          </div>
        </div>
        <span v-else>No current subscription</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :disabled="saving || resolvingUser"
          :aria-label="'Edit license ' + item.snumber"
          @click.stop="editItem(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" :persistent="saving" max-width="550px">
      <v-card>
        <v-form ref="editForm" @submit.prevent="save">
          <v-card-title>
            <span class="headline">Subscription License</span>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="saveError" type="error">{{ saveError }}</v-alert>
            <v-text-field
              :value="editedItem.snumber"
              label="License s/n"
              readonly
            ></v-text-field>
            <v-text-field
              :value="formatDate(editedItem.created_at)"
              label="Created"
              readonly
            ></v-text-field>
            <v-switch
              v-if="editedItem.has_legacy_license"
              v-model="editedItem.v3_enabled"
              label="Enabled"
              color="green darken-2"
              :disabled="saving"
              hint="Enabling this disables the linked legacy license."
              persistent-hint
              class="mb-4"
            ></v-switch>
            <v-text-field
              v-model="editedItem.info"
              label="Description"
              :disabled="saving"
            ></v-text-field>
            <v-text-field
              :value="editedBandwidth"
              label="Bandwidth"
              readonly
            ></v-text-field>
            <div v-if="editedItem.subscription" class="mb-4">
              <div>
                Subscription: {{ formatDate(editedItem.subscription.starts_at) }} —
                {{ formatDate(editedItem.subscription.ends_at) }}
              </div>
              <div>
                {{ formatCutoff(editedItem.subscription.br_release_cutoff) }}
              </div>
            </div>
            <v-text-field
              v-model="editedItem.passwd"
              label="New password"
              type="password"
              autocomplete="new-password"
              maxlength="32"
              :counter="32"
              :rules="passwordRules"
              :disabled="saving"
              hint="Leave blank to keep the current password."
              persistent-hint
            ></v-text-field>
            <v-file-input
              v-model="dmiLicenseFile"
              label="DMI.data"
              show-size
              :rules="dmiRules"
              :disabled="saving"
              hint="Optional hardware file, up to 8 KiB."
              persistent-hint
            ></v-file-input>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              :disabled="saving"
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn color="blue darken-1" text type="submit" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="trialDialog" :persistent="saving" max-width="550px">
      <v-card>
        <v-form ref="trialForm" @submit.prevent="saveTrial">
          <v-card-title>New trial license</v-card-title>
          <v-card-text>
            <v-alert type="info" text>
              To create a trial license for an existing user, first search for
              that user on the main page.
            </v-alert>
            <v-alert v-if="trialError" type="error">{{ trialError }}</v-alert>
            <v-checkbox
              v-model="trial.new_user"
              label="New user"
              :disabled="saving || !trialUser"
              @change="switchTrialUser"
            />
            <div v-if="!trialUser" class="mb-4">
              The user filters do not identify exactly one existing user.
              Enter the details of a new user below.
            </div>
            <v-text-field
              v-model="trial.u_email"
              label="User email"
              maxlength="100"
              :rules="trial.new_user ? emailRules : []"
              :disabled="saving || !trial.new_user"
            />
            <v-text-field
              v-model="trial.u_name"
              label="User name"
              maxlength="100"
              :rules="trial.new_user ? requiredRules : []"
              :disabled="saving || !trial.new_user"
            />
            <v-text-field
              v-model="trial.info"
              label="Description"
              :disabled="saving"
            />
            <v-text-field
              v-model="trial.bandwidth"
              label="Bandwidth (Gbit/s)"
              type="number"
              min="1"
              max="2147"
              step="1"
              :rules="trialBandwidthRules"
              :disabled="saving"
            />
            <div>
              The trial starts today and lasts 30 calendar days.
              Set the license password and upload DMI.data after creation.
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="saving" @click="trialDialog = false">Cancel</v-btn>
            <v-btn color="primary" type="submit" :loading="saving">Create</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { admin_http_client } from "../services/api.js";

// Match the backend's existing 8 KiB hardware-file limit.
const MAX_DMI_BYTES = 8 * 1024;
// Whole Gbit/s must fit in PostgreSQL int4 after conversion to kbit/s.
const MAX_TRIAL_GBIT = Math.floor(2147483647 / 1000000);
const emptyFilters = () => ({
  license_sn: "",
  user_id: "",
  user_email: "",
  user_name: "",
  v3_enabled: null,
  active_subscriptions: null
});
const emptyTrial = () => ({
  new_user: true,
  u_email: "",
  u_name: "",
  info: "",
  bandwidth: ""
});
const emptyLicense = () => ({
  id: null,
  info: "",
  snumber: "",
  created_at: null,
  v3_enabled: false,
  has_legacy_license: false,
  subscription: null,
  passwd: ""
});

export default {
  name: "admin_licenses_v3",

  data: () => ({
    filters: emptyFilters(),
    appliedFilters: {},
    hasSearched: false,
    total: 0,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ["created_at"],
      sortDesc: [true]
    },
    booleanOptions: [
      { text: "All", value: null },
      { text: "Yes", value: true },
      { text: "No", value: false }
    ],
    requestNumber: 0,
    trialRequestNumber: 0,
    resolvingUser: false,
    trialDialog: false,
    trialError: "",
    trialUser: null,
    trial: emptyTrial(),
    optionalIdRules: [
      value =>
        !value ||
        (/^[1-9][0-9]*$/.test(String(value)) && Number(value) <= 2147483647) ||
        "Enter a positive user ID."
    ],
    requiredRules: [
      value => (value && !!value.trim()) || "Required."
    ],
    emailRules: [
      value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim()) || "Enter a valid email address."
    ],
    trialBandwidthRules: [
      value =>
        (/^[1-9][0-9]*$/.test(String(value)) && Number(value) <= MAX_TRIAL_GBIT) ||
        "Enter a whole number from 1 to 2147 Gbit/s."
    ],
    licenses: [],
    loading: false,
    saving: false,
    dialog: false,
    loadError: "",
    saveError: "",
    message: "",
    dmiLicenseFile: null,
    editedItem: emptyLicense(),
    headers: [
      { text: "User", value: "user_name" },
      { text: "Description", value: "info", align: "start", sortable: false },
      { text: "License s/n", value: "snumber" },
      { text: "Created", value: "created_at" },
      { text: "Enabled", value: "v3_enabled" },
      { text: "Active subscriptions", value: "active_subscriptions" },
      { text: "Status", value: "status", sortable: true },
      { text: "Subscription", value: "subscription", sortable: false },
      { text: "Actions", value: "actions", sortable: false }
    ],
    passwordRules: [
      value => !value || value.length <= 32 || "Use at most 32 characters."
    ],
    dmiRules: [
      file =>
        !file ||
        (file.size > 0 && file.size <= MAX_DMI_BYTES) ||
        "Choose a nonempty file no larger than 8 KiB."
    ]
  }),

  computed: {
    editedBandwidth() {
      return this.editedItem.subscription
        ? this.formatBandwidth(this.editedItem.subscription.bandwidth)
        : "No current subscription";
    }
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.initialize();
      }
    },
    dialog(open) {
      if (!open) {
        this.editedItem = emptyLicense();
        this.dmiLicenseFile = null;
        this.saveError = "";
      }
    }
  },

  beforeDestroy() {
    this.requestNumber++;
    this.trialRequestNumber++;
  },

  methods: {
    async initialize() {
      // Table option changes must not load records before a search.
      if (!this.hasSearched) return;
      const request = ++this.requestNumber;
      this.loading = true;
      this.loadError = "";
      try {
        const response = await admin_http_client.get("/licenses_v3", {
          params: {
            ...this.appliedFilters,
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            sort_by: this.options.sortBy[0] || "created_at",
            descending: this.options.sortBy.length ? !!this.options.sortDesc[0] : true
          }
        });
        if (request !== this.requestNumber) return;
        this.licenses = response.data.items;
        this.total = response.data.total;
        const lastPage = Math.max(1, Math.ceil(this.total / this.options.itemsPerPage));
        if (this.options.page > lastPage) this.options.page = lastPage;
      } catch (error) {
        if (request !== this.requestNumber) return;
        this.licenses = [];
        this.total = 0;
        this.loadError = this.errorMessage(error, "Unable to load licenses. Please try again.");
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },

    filterParams() {
      const params = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        const normalized = typeof value === "string" ? value.trim() : value;
        if (normalized !== "" && normalized !== null && normalized !== undefined) {
          params[key] = normalized;
        }
      });
      return params;
    },

    searchLicenses() {
      if (!this.$refs.searchForm.validate()) return;
      this.appliedFilters = this.filterParams();
      this.hasSearched = true;
      if (this.options.page !== 1) this.options.page = 1;
      else this.initialize();
    },

    resetSearch() {
      this.hasSearched = false;
      // Ignore any response from a search that was cleared.
      this.requestNumber++;
      this.filters = emptyFilters();
      this.appliedFilters = {};
      this.licenses = [];
      this.total = 0;
      this.loading = false;
      this.loadError = "";
      this.message = "";
      this.options.page = 1;
      this.$nextTick(() => this.$refs.searchForm.resetValidation());
    },

    async openTrial() {
      if (this.saving || this.resolvingUser || !this.$refs.searchForm.validate()) return;
      const request = ++this.trialRequestNumber;
      this.resolvingUser = true;
      this.loadError = "";
      this.message = "";
      const filters = this.filterParams();
      const params = {};
      ["user_id", "user_email", "user_name"].forEach(key => {
        if (filters[key] !== undefined) params[key] = filters[key];
      });
      try {
        // Resolve only the user fields currently entered in the parent search.
        const response = await admin_http_client.get("/licenses_v3/trial_user", { params });
        if (request !== this.trialRequestNumber) return;
        this.trialUser = response.data;
        this.trial = emptyTrial();
        this.trial.new_user = !this.trialUser;
        this.switchTrialUser();
        this.trialError = "";
        this.trialDialog = true;
        this.$nextTick(() => {
          if (this.$refs.trialForm) this.$refs.trialForm.resetValidation();
        });
      } catch (error) {
        if (request !== this.trialRequestNumber) return;
        this.loadError = this.errorMessage(error, "Unable to look up the user. Please try again.");
      } finally {
        if (request === this.trialRequestNumber) this.resolvingUser = false;
      }
    },

    switchTrialUser() {
      if (!this.trialUser) this.trial.new_user = true;
      this.trial.u_email = this.trial.new_user ? "" : this.trialUser.email || "";
      this.trial.u_name = this.trial.new_user ? "" : this.trialUser.name || "";
      this.trialError = "";
      this.$nextTick(() => {
        if (this.$refs.trialForm) this.$refs.trialForm.resetValidation();
      });
    },

    async saveTrial() {
      if (this.saving || !this.$refs.trialForm.validate()) return;
      this.saving = true;
      this.trialError = "";
      const payload = {
        new_user: this.trial.new_user,
        info: this.trial.info,
        bandwidth: Number(this.trial.bandwidth)
      };
      if (this.trial.new_user) {
        payload.u_email = this.trial.u_email.trim();
        payload.u_name = this.trial.u_name.trim();
      } else {
        payload.user_id = this.trialUser.id;
      }
      try {
        const response = await admin_http_client.post("/licenses_v3/trial", payload);
        this.trialDialog = false;
        this.message = "Trial license has been successfully created.";
        // Show the user's licenses, with the new trial first, even if previous
        // license or subscription filters would have hidden it.
        this.filters = { ...emptyFilters(), user_id: String(response.data.user_id) };
        this.appliedFilters = this.filterParams();
        this.hasSearched = true;
        this.options = {
          ...this.options,
          page: 1,
          sortBy: ["created_at"],
          sortDesc: [true]
        };
      } catch (error) {
        this.trialError = this.errorMessage(error, "Unable to create the trial license. Please try again.");
      } finally {
        this.saving = false;
      }
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC"
      });
    },

    formatBandwidth(value) {
      // Subscription bandwidth is stored in kbit/s.
      if (value >= 1000000) return value / 1000000 + " Gbit/s";
      if (value >= 1000) return value / 1000 + " Mbit/s";
      return value + " kbit/s";
    },

    formatCutoff(value) {
      return value ? "BR releases through " + this.formatDate(value) : "All BR releases";
    },

    editItem(item) {
      if (this.saving || this.resolvingUser || this.trialDialog) return;
      this.editedItem = { ...item, passwd: "" };
      this.dmiLicenseFile = null;
      this.saveError = "";
      this.message = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
      });
    },

    onDoubleClick(event, row) {
      this.editItem(row.item);
    },

    errorMessage(error, fallback) {
      const detail =
        error.response && error.response.data && error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join("; ");
      if (error.response && error.response.status === 403)
        return "Access denied. Check your administrator account and the nginx IP allowlist.";
      return fallback;
    },

    update_license_v3(license_id, info, passwd, dmi_license_file, v3_enabled) {
      const form_data = new FormData();
      form_data.append("info", info);
      form_data.append("passwd", passwd);
      if (typeof v3_enabled === "boolean") {
        form_data.append("v3_enabled", v3_enabled ? "true" : "false");
      }
      if (dmi_license_file) {
        form_data.append("dmi_license_file", dmi_license_file);
      }
      return admin_http_client.post(`/licenses_v3/${license_id}`, form_data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },

    async save() {
      if (this.saving || !this.$refs.editForm.validate()) return;
      this.saving = true;
      this.saveError = "";
      const serial = this.editedItem.snumber;
      try {
        await this.update_license_v3(
          this.editedItem.id,
          this.editedItem.info,
          this.editedItem.passwd,
          this.dmiLicenseFile,
          this.editedItem.has_legacy_license
            ? this.editedItem.v3_enabled
            : undefined
        );
        // Reflect the confirmed edit even if refreshing the list later fails.
        const item = this.licenses.find(
          license => license.id === this.editedItem.id
        );
        if (item) {
          item.info = this.editedItem.info;
          item.v3_enabled = this.editedItem.v3_enabled;
          item.status = item.v3_enabled && Boolean(item.subscription);
        }
        this.dialog = false;
        this.message = "License #" + serial + " has been successfully updated.";
        await this.initialize();
      } catch (error) {
        this.saveError = this.errorMessage(
          error,
          "Unable to save the license. Please try again."
        );
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
